/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import JSZip from 'jszip';
import pptxgen from 'pptxgenjs';
import { RefObject } from 'react';
import {
  CumulativeUsageByNetwork,
  TotalUsageByLink,
  TotalUsageByNetwork,
  UsageByLink,
  UsageByLinkCumulative,
  UsageByNetwork,
  TotalPacketLossByLink,
  ServiceAvailability
} from '../../../shared/entities/dataUsage';
import { EMPTY_FIELD_STRING_PLACEHOLDER } from '../../../utils/StringUtils';
import { calculateTotalServiceAvailabilityPercentage } from '../DataUsageContainer';
import { getSeriesData } from '../charts/ServiceAvailabilityChart';

export type ExportFormatType = 'PowerPoint (.ppt)' | 'Image (.png)' | 'PDF (.pdf)';
export type ChartExportType =
  | 'Link Usage'
  | 'Link Usage (cumulative)'
  | 'Link Usage (comparison)'
  | 'Network Usage'
  | 'Network Usage (cumulative)'
  | 'Network Usage (comparison)'
  | 'Performance Summary'
  | 'Service Availability';
export const exportChartsToZip = (
  fromMsSinceEpoch: number,
  toMsSinceEpoch: number,
  chartRefs: {
    name: string;
    displayName: string;
    chartExportType: ChartExportType;
    ref: RefObject<HTMLDivElement | undefined>;
  }[],
  selectedExportFormatTypes: ExportFormatType[] | string[],
  selectedChartExportTypes: ChartExportType[] | string[],
  reportName: string,
  totalUsageByNetwork: TotalUsageByNetwork | undefined,
  usageByNetwork: UsageByNetwork | undefined,
  totalUsageByLink: TotalUsageByLink | undefined,
  usageByLink: UsageByLink | undefined,
  cumulativeUsageByLink: UsageByLink | undefined,
  cumulativeUsageByNetwork: CumulativeUsageByNetwork | undefined,
  totalPacketLoss: TotalPacketLossByLink | undefined,
  serviceAvailability: ServiceAvailability | undefined,
) => {
  const chartPromises: Promise<{
    exportFormatType: ExportFormatType;
    type: string;
    name: string;
    dataURL: string | jsPDF | null | string | void | ArrayBuffer | Blob | Uint8Array;
    width?: number;
    height?: number;
  }>[] = [];

  // Build the charts
  chartRefs.forEach(
    (chartRef: {
      name: string;
      displayName: string;
      chartExportType: ChartExportType;
      ref: RefObject<HTMLDivElement | undefined>;
    }) => {
      if (chartRef.ref && selectedChartExportTypes.includes(chartRef.chartExportType)) {
        if (selectedExportFormatTypes.includes('Image (.png)') || selectedExportFormatTypes.includes('PDF (.pdf)')) {
          chartPromises.push(
            getImageBase64(chartRef.ref?.current).then((value: string | null) => ({
              exportFormatType: 'Image (.png)',
              type: 'image',
              name: chartRef.name,
              dataURL: value,
              width: chartRef.ref.current?.clientWidth,
              height: chartRef.ref.current?.clientHeight,
            })),
          );
        }
        if (selectedExportFormatTypes.includes('PDF (.pdf)')) {
          chartPromises.push(
            getPdfBase64(chartRef.ref?.current).then((value: string | jsPDF | null) => ({
              exportFormatType: 'PDF (.pdf)',
              type: 'pdf',
              name: chartRef.name,
              dataURL: value,
            })),
          );
        }
      }
    },
  );
  if (selectedExportFormatTypes.includes('PowerPoint (.ppt)')) {
    chartPromises.push(
      getPowerPointBase64(
        totalUsageByNetwork,
        usageByNetwork,
        cumulativeUsageByNetwork,
        totalUsageByLink,
        usageByLink,
        cumulativeUsageByLink,
        totalPacketLoss,
        serviceAvailability,
        selectedChartExportTypes,
      ).then((value: string | void | ArrayBuffer | Blob | Uint8Array) => ({
        exportFormatType: 'PowerPoint (.ppt)',
        type: 'ppt',
        name: reportName,
        dataURL: value,
      })),
    );
  }

  const imageArtifactsBase64: {
    fileName: string;
    dataURL: string | null;
    width: number | undefined;
    height: number | undefined;
  }[] = [];
  const pptArtifactsBase64: { fileName: string; dataURL: string | void | ArrayBuffer | Blob | Uint8Array }[] = [];
  Promise.all([...chartPromises]).then((values: any[]) => {
    values.forEach((value: { type: string; name: string; dataURL: string; width?: number; height?: number }) => {
      if (value.type === 'ppt') {
        pptArtifactsBase64.push({
          fileName: value.name,
          dataURL: value.dataURL,
        });
      } else if (value.type === 'image') {
        imageArtifactsBase64.push({
          fileName: value.name,
          dataURL: value.dataURL,
          width: value.width,
          height: value.height,
        });
      }
    });
    generateZip(
      fromMsSinceEpoch,
      toMsSinceEpoch,
      selectedExportFormatTypes,
      reportName,
      imageArtifactsBase64,
      pptArtifactsBase64,
    );
  });
};

export const getPowerPointBase64 = (
  totalUsageByNetwork: TotalUsageByNetwork | undefined,
  usageByNetwork: UsageByNetwork | undefined,
  cumulativeUsageByNetwork: CumulativeUsageByNetwork | undefined,
  totalUsageByLink: TotalUsageByLink | undefined,
  usageByLink: UsageByLink | undefined,
  cumulativeUsageByLink: UsageByLinkCumulative | undefined,
  totalPacketLoss: TotalPacketLossByLink | undefined,
  serviceAvailability: ServiceAvailability | undefined,
  selectedChartExportTypes: ChartExportType[] | string[],
): Promise<string | void | ArrayBuffer | Blob | Uint8Array> => {
  
  const roundedPacketLoss = totalPacketLoss ? Math.round(totalPacketLoss.lossPercentage * 10) / 10 : undefined;

  let pres = new pptxgen();
  if (selectedChartExportTypes.includes('Network Usage (comparison)')) {
    buildSlide(
      pres,
      'doughnut',
      {
        x: 1,
        y: 1,
        w: 8,
        h: 4,
      },
      `Network Usage (comparison) (${new Date(usageByNetwork?.fromMsSinceEpoch || 0)
        .toISOString()
        .substring(0, 10)} to ${new Date(usageByNetwork?.toMsSinceEpoch || 0).toISOString().substring(0, 10)})`,
      [
        {
          name: 'Total Network Usage Bytes',
          labels: ['Ka, Ku'],
          values: [totalUsageByNetwork?.kaUsageBytes || 0, totalUsageByNetwork?.kuUsageBytes] || 0,
        },
      ],
    );
  }
  if (selectedChartExportTypes.includes('Network Usage')) {
    buildSlide(
      pres,
      'line',
      { x: 1, y: 1, w: 8, h: 4 },
      `Network Usage (${new Date(usageByNetwork?.fromMsSinceEpoch || 0)?.toISOString()?.substring(0, 10)} to ${new Date(
        usageByNetwork?.toMsSinceEpoch || 0,
      )
        .toISOString()
        .substring(0, 10)})`,
      [
        {
          name: 'Ka Usage Bytes',
          labels: Object.keys(usageByNetwork?.kaUsageBytes || {}),
          values: Object.keys(usageByNetwork?.kaUsageBytes || {}).map(
            (sourceTime: string) => usageByNetwork?.kaUsageBytes[sourceTime],
          ),
        },
        {
          name: 'Ku Usage Bytes',
          labels: Object.keys(usageByNetwork?.kuUsageBytes || {}),
          values: Object.keys(usageByNetwork?.kaUsageBytes || {}).map(
            (sourceTime: string) => usageByNetwork?.kuUsageBytes[sourceTime],
          ),
        },
      ],
    );
  }
  if (selectedChartExportTypes.includes('Network Usage (cumulative)')) {
    buildSlide(
      pres,
      'line',
      { x: 1, y: 1, w: 8, h: 4 },
      `Network Usage (Cumulative) (${new Date(cumulativeUsageByNetwork?.fromMsSinceEpoch || 0)
        .toISOString()
        .substring(0, 10)} to ${new Date(cumulativeUsageByNetwork?.toMsSinceEpoch || 0)
        .toISOString()
        .substring(0, 10)})`,
      [
        {
          name: 'Ka Usage Bytes',
          labels: Object.keys(cumulativeUsageByNetwork?.kaCumulativeUsageBytes || {}),
          values: Object.keys(cumulativeUsageByNetwork?.kaCumulativeUsageBytes || {}).map(
            (sourceTime: string) => cumulativeUsageByNetwork?.kaCumulativeUsageBytes[sourceTime],
          ),
        },
        {
          name: 'Ku Usage Bytes',
          labels: Object.keys(cumulativeUsageByNetwork?.kuCumulativeUsageBytes || {}),
          values: Object.keys(cumulativeUsageByNetwork?.kuCumulativeUsageBytes || {}).map(
            (sourceTime: string) => cumulativeUsageByNetwork?.kuCumulativeUsageBytes[sourceTime],
          ),
        },
      ],
    );
  }
  if (selectedChartExportTypes.includes('Link Usage (comparison)')) {
    buildSlide(
      pres,
      'doughnut',
      {
        x: 1,
        y: 1,
        w: 8,
        h: 4,
      },
      `Link Usage (comparison) (${new Date(totalUsageByLink?.fromMsSinceEpoch || 0)
        .toISOString()
        .substring(0, 10)} to ${new Date(totalUsageByLink?.toMsSinceEpoch || 0).toISOString().substring(0, 10)})`,
      [
        {
          name: 'Total Link Usage Bytes',
          labels: ['Forward Link, Return Link'],
          values: [totalUsageByLink?.flUsageBytes || 0, totalUsageByLink?.rlUsageBytes] || 0,
        },
      ],
    );
  }
  if (selectedChartExportTypes.includes('Link Usage')) {
    buildSlide(
      pres,
      'line',
      { x: 1, y: 1, w: 8, h: 4 },
      `Link Usage (${new Date(usageByNetwork?.fromMsSinceEpoch || 0)?.toISOString()?.substring(0, 10)} to ${new Date(
        usageByNetwork?.toMsSinceEpoch || 0,
      )
        .toISOString()
        .substring(0, 10)})`,
      [
        {
          name: 'Forward Link Usage Bytes',
          labels: Object.keys(usageByLink?.usageByLinkBytes || {}),
          values: Object.keys(usageByLink?.usageByLinkBytes || {}).map(
            (sourceTime: string) => usageByLink?.usageByLinkBytes[sourceTime]?.flUsageBytes,
          ),
        },
        {
          name: 'Return Link Usage Bytes',
          labels: Object.keys(usageByLink?.usageByLinkBytes || {}),
          values: Object.keys(usageByLink?.usageByLinkBytes || {}).map(
            (sourceTime: string) => usageByLink?.usageByLinkBytes[sourceTime].rlUsageBytes,
          ),
        },
      ],
    );
  }
  if (selectedChartExportTypes.includes('Link Usage (cumulative)')) {
    buildSlide(
      pres,
      'line',
      { x: 1, y: 1, w: 8, h: 4 },
      `Link Usage (Cumulative) (${new Date(cumulativeUsageByLink?.fromMsSinceEpoch || 0)
        .toISOString()
        .substring(0, 10)} to ${new Date(cumulativeUsageByLink?.toMsSinceEpoch || 0).toISOString().substring(0, 10)})`,
      [
        {
          name: 'Forward Link Usage Bytes (Cumulative) ',
          labels: Object.keys(cumulativeUsageByLink?.usageByLinkBytes || {}),
          values: Object.keys(cumulativeUsageByLink?.usageByLinkBytes || {}).map(
            (sourceTime: string) => cumulativeUsageByLink?.usageByLinkBytes[sourceTime]?.flUsageBytes,
          ),
        },
        {
          name: 'Return Link Usage Bytes (Cumulative) ',
          labels: Object.keys(cumulativeUsageByLink?.usageByLinkBytes || {}),
          values: Object.keys(cumulativeUsageByLink?.usageByLinkBytes || {}).map(
            (sourceTime: string) => cumulativeUsageByLink?.usageByLinkBytes[sourceTime].rlUsageBytes,
          ),
        },
      ],
    );
  }
  
  if (selectedChartExportTypes.includes('Performance Summary')) {
    buildTextSlide(
      pres,
      `Performance Summary`,
      ['Packet Loss', 'Service Availability'],
      [roundedPacketLoss, serviceAvailability ? calculateTotalServiceAvailabilityPercentage(serviceAvailability) : undefined]
    );
  }

  if (selectedChartExportTypes.includes('Service Availability')) {
    buildSlide(
      pres,
      'bar',
      { x: 1, y: 1, w: 8, h: 4 , valAxisMaxVal:100},
      `Service Availability (${new Date(cumulativeUsageByLink?.fromMsSinceEpoch || 0)
        .toISOString()
        .substring(0, 10)} to ${new Date(cumulativeUsageByLink?.toMsSinceEpoch || 0).toISOString().substring(0, 10)})`,
      [
        {
          name: 'Service Availability',
          labels: getSeriesData(serviceAvailability?.serviceAvailability ?? []).data.map(item => item.x.toString()),
          values: getSeriesData(serviceAvailability?.serviceAvailability ?? []).data.map(item => item.y)
        },
      ],
    );
  }

  return pres
    .write({ outputType: 'base64' })
    .then(data => {
      return data;
    })
    .catch(err => {
      console.error(err);
    });
};

const buildSlide = (
  pres: pptxgen,
  chartType: 'doughnut' | 'line' | 'bar',
  chartOptions: pptxgen.IChartOpts | undefined,
  title: string,
  chartData: {
    name: string;
    labels: string[];
    values: (number | undefined)[];
  }[],
): void => {
  const slide = pres.addSlide();
  // 3. Add 1+ objects (Tables, Shapes, etc.) to the Slide
  slide.addText(title, {
    x: 1.5,
    y: 0.5,
    color: '363636',
    fill: { color: 'F1F1F1' },
    align: pres.AlignH.center,
  });

  var outType = pres.ChartType.doughnut;

  if(chartType === 'line') {
    outType = pres.ChartType.line;
  } else if(chartType === 'bar') { 
    outType = pres.ChartType.bar; 
  }

  slide.addChart(outType, chartData, chartOptions);
};

const buildTextSlide = (
  pres: pptxgen,
  title: string,
  labels: string[],
  values: (number | undefined)[],
): void => {
  const slide = pres.addSlide();
  slide.addText(title, {
    x: 1.5,
    y: 0.5,
    color: '363636',
    fill: { color: 'F1F1F1' },
    align: pres.AlignH.center,
  });

  for(let i = 0; i < labels.length; i++) {
    slide.addText(`${values[i]?.toString()}%` || EMPTY_FIELD_STRING_PLACEHOLDER, {
      x: 3 * i,
      y: "50%",
      color: '363636',
      fill: { color: 'F1F1F1' },
      align: pres.AlignH.center,
    });
    slide.addText(labels[i], {
      x: 3 * i,
      y: "60%",
      color: '363636',
      fill: { color: 'F1F1F1' },
      align: pres.AlignH.center,
    });
  }
};

const getPdfBase64 = (element: HTMLElement | undefined | null): Promise<string | jsPDF | null> => {
  if (element) {
    return html2canvas(element).then(function (canvas: any) {
      var dataURL = canvas.toDataURL('image/jpeg', 1.0);
      var pdf = new jsPDF('l', 'px', [element.clientWidth, element.clientHeight]);
      pdf.addImage(dataURL, 0, 0, element.clientWidth, element.clientHeight);
      return pdf.output('datauristring');
    });
  } else {
    return Promise.resolve(null);
  }
};

const getImageBase64 = (element: HTMLElement | undefined | null): Promise<string | null> => {
  if (element) {
    return html2canvas(element).then(function (canvas: any) {
      return canvas.toDataURL('image/png', 1.0);
    });
  } else {
    return Promise.resolve(null);
  }
};

const generateZip = (
  fromMsSinceEpoch: number,
  toMsSinceEpoch: number,
  selectedExportFormatTypes: ExportFormatType[] | string[],
  reportName: string,
  imageDataURLs: { fileName: string; dataURL: string | null; width: number | undefined; height: number | undefined }[],
  pptDataURLs: { fileName: string; dataURL: string | void | ArrayBuffer | Blob | Uint8Array }[],
) => {
  var zip = new JSZip();
  const images = selectedExportFormatTypes.includes('Image (.png)') ? zip.folder('Image') : null;
  const ppts = selectedExportFormatTypes.includes('PowerPoint (.ppt)') ? zip.folder('PowerPoint') : null;
  const pdfs = selectedExportFormatTypes.includes('PDF (.pdf)') ? zip.folder('PDF') : null;
  if (ppts) {
    pptDataURLs.forEach(
      (pptDataURL: { fileName: string; dataURL: string | void | ArrayBuffer | Blob | Uint8Array }) => {
        if (pptDataURL.dataURL) {
          ppts.file(
            `UP Data Report ${new Date(fromMsSinceEpoch)?.toISOString()?.substring(0, 10)}-${new Date(toMsSinceEpoch)
              .toISOString()
              .substring(0, 10)}.ppt`,
            pptDataURL.dataURL,
            {
              base64: true,
            },
          );
        }
      },
    );
  }
  if (pdfs) {
    var pdf = new jsPDF({
      orientation: 'l',
      unit: 'px',
      format: [imageDataURLs[0].width || 1000, imageDataURLs[0].height || 1000],
    });

    imageDataURLs.forEach(
      (
        imageDataURL: {
          fileName: string;
          dataURL: string | null;
          width: number | undefined;
          height: number | undefined;
        },
        index: number,
        array: {
          fileName: string;
          dataURL: string | null;
          width: number | undefined;
          height: number | undefined;
        }[],
      ) => {
        if (imageDataURL.dataURL) {
          pdf.addPage([imageDataURL.width || 1000, imageDataURL.height || 1000]);
          pdf.addImage(imageDataURL.dataURL, 'PNG', 0, 0, imageDataURL.width || 1000, imageDataURL.height || 440);
        }
      },
      pdf.deletePage(1),
    );
    pdfs.file(
      `UP Data Report ${new Date(fromMsSinceEpoch)?.toISOString()?.substring(0, 10)}-${new Date(toMsSinceEpoch)
        .toISOString()
        .substring(0, 10)}.pdf`,
      pdf.output('datauristring').replace(/^data:application\/pdf;filename=generated.pdf;base64,/, ''),
      {
        base64: true,
      },
    );
  }
  if (images) {
    imageDataURLs.forEach((imageDataURL: { fileName: string; dataURL: string | null }) => {
      if (imageDataURL.dataURL) {
        images.file(
          `${imageDataURL.fileName}.png`,
          imageDataURL.dataURL.replace(/^data:image\/(png|jpg);base64,/, ''),
          {
            base64: true,
          },
        );
      }
    });
  }
  zip.generateAsync({ type: 'blob' }).then(function (content: any) {
    // see FileSaver.js
    saveAs(content, `${reportName}.zip`);
  });

  // a.click();
};
